import { ReactNode } from 'react';

export type Params<T = string | number> = {
  [key: string]: Params<T> | T | undefined;
};

export class Locale {
  public readonly locale: string;

  constructor(locale: string) {
    this.locale = locale;
  }

  t(template: string, params: Params): string {
    return template.replace(
      /([$%]){(.+?)(,.+?)?}/g,
      (match, type, key, args) => {
        const value = get(params, key);
        if (typeof value === 'object') return '⬞';
        if (type === '%') {
          const plural = value === 1 ? 'one' : 'other';
          return args.match(`,${plural}:([^,]+)`)[1];
        }
        return value;
      },
    );
  }
}

/**
 * Create a localised string from a template
 *
 * ${foo.bar} will be replaced with params.foo.bar
 *
 * %{day,one:day,other:days} will be replaced with either 'day' or 'days',
 * depending on the value of params.day
 */
export function t(template: string, params: Params<ReactNode>): ReactNode {
  let lastIndex = 0;
  const result = [];
  for (const match of template.matchAll(/([$%]){(.+?)(,.+?)?}/g)) {
    const [x, type, key, args] = match;
    result.push(template.slice(lastIndex, match.index));
    lastIndex = match.index! + x.length;
    let value = get(params, key);
    if (type === '%') {
      const plural = value === 1 ? 'one' : 'other';
      value = args.match(`,${plural}:([^,]+)`)?.[1];
    }
    result.push(value);
  }
  result.push(template.slice(lastIndex));
  return result;
}

function get<T>(obj: Params<T>, key: string): T | string {
  let current: Params<T | string> | T | undefined = obj;
  const parts = key.split('.');
  for (const part of parts) {
    // just assume obj is an object as long as there are keys to resolve
    current = (current as Params<T>)?.[part];
  }
  return (current as T) ?? '⬞';
}

/**
 * Reverse of Intl.NumberFormat.format()
 */
export function parseNumber(
  numberFormat: Intl.NumberFormat,
  value: string,
): number {
  const nonDigit = /[^\d]/g;
  const decimalSeparator = numberFormat.format(0.1)[1];
  const split = value.indexOf(decimalSeparator);
  if (split === -1) {
    return +value.replace(nonDigit, '');
  }

  const decimal = value.slice(0, split);
  const fraction = value.slice(split + 1);
  return +`${decimal.replace(nonDigit, '')}.${fraction.replace(nonDigit, '')}`;
}

/**
 * Returns the two-letter lowercase language code of a language range or prefix
 * (e.g. "en-HK" -> "en", "zh_TW" -> "zh", "/en-hk" -> "en"). Strips any
 * non-alpha characters first so separators, leading slashes, and region
 * suffixes don't leak in.
 */
export function languageCode(localeOrPrefix: string): string {
  return localeOrPrefix
    .replace(/[^a-z]/gi, '')
    .slice(0, 2)
    .toLowerCase();
}

/**
 * Normalise the region subtag of a language range to uppercase so the value is
 * a canonical BCP-47 tag (`zh-hk` -> `zh-HK`), e.g. for JSON-LD `inLanguage`.
 * The CMS `lang` uses a lowercase two-letter region; anything else (no region,
 * or a script subtag like `zh-Hant-HK`) is returned unchanged.
 */
export function toBcp47(lang: string): string {
  return lang.replace(
    /-([a-z]{2})$/,
    (_, region) => `-${region.toUpperCase()}`,
  );
}
